import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Clock,
  MessageSquare,
  Trophy,
  Target,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { supabase } from '../../utils/supabase/client';
import { getStudentSession } from '../../utils/supabase/auth';
import { toast } from 'sonner';

type SlideContent = Record<string, any>;

type ActivityDetail = {
  id: string;
  sessionId: string;
  slideId: string;
  slideType: string;
  lessonTitle: string;
  title: string;
  score: number | null;
  maxScore: number | null;
  graded: boolean;
  createdAt: string;
  answer: Record<string, any>;
  slideContent: SlideContent;
};

type Question = {
  question: string;
  options: string[];
  correctAnswer?: number | boolean | string;
};

const autoGradedTypes = new Set([
  'multiple_choice',
  'quiz',
  'true_false',
  'matching_pairs',
  'time_to_climb',
  'fill_blanks',
]);

const getMaxScore = (content: SlideContent): number | null => {
  if (typeof content?.points === 'number') return content.points;
  if (typeof content?.maxPoints === 'number') return content.maxPoints;
  return null;
};

const formatTimestamp = (value?: string) => {
  if (!value) return 'Unknown time';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown time';
  return date.toLocaleString();
};

export default function StudentActivityFeedback() {
  const { activityId } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState<ActivityDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activityId) {
      setError('Activity not found');
      setLoading(false);
      return;
    }

    const loadActivity = async () => {
      try {
        setLoading(true);
        setError(null);
        const studentSession = getStudentSession();
        if (!studentSession) {
          setError('Please join a session first');
          return;
        }

        const { data, error: fetchError } = await supabase
          .from('submissions')
          .select(`
            id,
            session_id,
            slide_id,
            answer,
            score,
            graded,
            created_at,
            slides (
              id,
              type,
              content,
              lessons (
                title
              )
            )
          `)
          .eq('id', activityId)
          .maybeSingle();

        if (fetchError) throw fetchError;
        if (!data) {
          setError('Activity not found');
          return;
        }

        const slide = (data as any).slides;
        const slideContent: SlideContent = slide?.content || {};
        const title =
          slideContent.title ||
          slideContent.question ||
          slideContent.prompt ||
          slideContent.instructions ||
          'Activity';

        setActivity({
          id: data.id,
          sessionId: data.session_id,
          slideId: data.slide_id,
          slideType: slide?.type || 'activity',
          lessonTitle: slide?.lessons?.title || 'Lesson',
          title,
          score: data.score ?? null,
          maxScore: getMaxScore(slideContent),
          graded: !!data.graded,
          createdAt: data.created_at,
          answer: data.answer || {},
          slideContent,
        });
      } catch (err) {
        console.error('Error loading activity feedback:', err);
        setError('Failed to load activity feedback');
        toast.error('Failed to load activity feedback');
      } finally {
        setLoading(false);
      }
    };

    loadActivity();
  }, [activityId]);

  const questions = useMemo<Question[]>(() => {
    if (!activity) return [];
    const content = activity.slideContent;

    if (activity.slideType === 'time_to_climb' || activity.slideType === 'quiz') {
      return Array.isArray(content?.questions) ? content.questions : [];
    }

    if (activity.slideType === 'multiple_choice') {
      return [
        {
          question: content?.question || 'Question',
          options: content?.options || [],
          correctAnswer: content?.correctAnswer,
        },
      ];
    }

    if (activity.slideType === 'true_false') {
      return [
        {
          question: content?.question || 'True or False',
          options: ['True', 'False'],
          correctAnswer: content?.correctAnswer === true ? 0 : 1,
        },
      ];
    }

    return [];
  }, [activity]);

  const getAnswerIndex = (index: number) => {
    if (!activity) return null;
    const answer = activity.answer || {};
    const answers = answer.answers || {};
    const mapValue = answers[index] ?? answers[String(index)];

    if (typeof mapValue === 'number') return mapValue;

    const selectedOption = answer.selectedOption ?? answer.answer;
    if (typeof selectedOption === 'number') return selectedOption;
    if (typeof selectedOption === 'boolean') return selectedOption ? 0 : 1;
    return null;
  };

  const renderQuestionReview = () => {
    if (!activity || questions.length === 0) return null;

    const isIdentification = activity.slideType === 'time_to_climb' && activity.slideContent?.quizType === 'identification';

    return (
      <div className="bg-card border border-border rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
        <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">Question Review</h3>
        <div className="space-y-3 sm:space-y-4">
          {questions.map((q, index) => {
            // For identification, compare strings; for MC, compare indices
            if (isIdentification) {
              const answer = activity.answer || {};
              const answers = answer.answers || {};
              const studentAnswer = String(answers[index] ?? answers[String(index)] ?? '').trim();
              const correctAnswer = String(q.correctAnswer ?? '').trim();
              const isCorrect = studentAnswer.toLowerCase() === correctAnswer.toLowerCase();

              return (
                <div
                  key={`${q.question}-${index}`}
                  className={`border-2 rounded-xl p-3 sm:p-4 ${
                    isCorrect
                      ? 'border-green-200 bg-green-50'
                      : 'border-red-200 bg-red-50'
                  }`}
                >
                  <div className="flex items-start gap-2 sm:gap-3">
                    {isCorrect ? (
                      <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0 mt-1" />
                    ) : (
                      <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0 mt-1" />
                    )}
                    <div className="flex-1">
                      <p className="font-semibold text-xs sm:text-base text-foreground mb-2 sm:mb-3">
                        Question {index + 1}: {q.question}
                      </p>
                      <div className="space-y-1 sm:space-y-2">
                        <div className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm ${
                          isCorrect ? 'bg-green-100 border border-green-300' : 'bg-blue-100 border border-blue-300'
                        }`}>
                          <div className="flex items-center justify-between gap-2">
                            <span>Your answer: {studentAnswer || '(no answer)'}</span>
                            <Badge className="ml-1 text-xs flex-shrink-0">Your answer</Badge>
                          </div>
                        </div>
                        {!isCorrect && (
                          <div className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm bg-green-100 border border-green-300 font-semibold">
                            <div className="flex items-center justify-between gap-2">
                              <span>Correct answer: {correctAnswer}</span>
                              <Badge className="ml-1 text-xs bg-green-600 flex-shrink-0">Correct</Badge>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            // Multiple choice rendering (original)
            const answerIndex = getAnswerIndex(index);
            const isCorrect =
              typeof q.correctAnswer === 'number' && answerIndex !== null
                ? answerIndex === q.correctAnswer
                : null;

            return (
              <div
                key={`${q.question}-${index}`}
                className={`border-2 rounded-xl p-3 sm:p-4 ${
                  isCorrect === true
                    ? 'border-green-200 bg-green-50'
                    : isCorrect === false
                    ? 'border-red-200 bg-red-50'
                    : 'border-border bg-card'
                }`}
              >
                <div className="flex items-start gap-2 sm:gap-3">
                  {isCorrect === true && (
                    <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0 mt-1" />
                  )}
                  {isCorrect === false && (
                    <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0 mt-1" />
                  )}
                  <div className="flex-1">
                    <p className="font-semibold text-xs sm:text-base text-foreground mb-2 sm:mb-3">
                      Question {index + 1}: {q.question}
                    </p>

                    <div className="space-y-1 sm:space-y-2">
                      {(q.options || []).map((option, optionIndex) => {
                        const isStudentAnswer = optionIndex === answerIndex;
                        const isCorrectAnswer = optionIndex === q.correctAnswer;

                        return (
                          <div
                            key={`${option}-${optionIndex}`}
                            className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm ${
                              isCorrectAnswer
                                ? 'bg-green-100 border border-green-300 font-semibold'
                                : isStudentAnswer
                                ? 'bg-blue-100 border border-blue-300'
                                : 'bg-white border border-gray-200'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate">{option}</span>
                              {isStudentAnswer && (
                                <Badge className="ml-1 text-xs flex-shrink-0">Your answer</Badge>
                              )}
                              {isCorrectAnswer && (
                                <Badge className="ml-1 text-xs bg-green-600 flex-shrink-0">Correct</Badge>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAnswerDetails = () => {
    if (!activity) return null;
    const answer = activity.answer || {};
    const content = activity.slideContent;

    if (activity.slideType === 'draw_it' && answer.drawing) {
      return (
        <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">Your Submission</h3>
          <img
            src={answer.drawing}
            alt="Student drawing"
            className="w-full rounded-xl border-2 border-border shadow-lg"
          />
        </div>
      );
    }

    if (activity.slideType === 'open_ended') {
      return (
        <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">Your Response</h3>
          <p className="text-sm sm:text-base text-foreground whitespace-pre-wrap">
            {answer.answer || 'No response submitted.'}
          </p>
        </div>
      );
    }

    if (activity.slideType === 'fill_blanks') {
      const correctAnswers: string[] = content?.answers || [];
      const studentAnswers = answer.answers || {};
      return (
        <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">Your Answers</h3>
          <div className="space-y-2">
            {correctAnswers.map((correct, index) => (
              <div key={index} className="flex items-center justify-between gap-2 text-xs sm:text-sm">
                <span className="text-muted-foreground">Blank {index + 1}</span>
                <span className="font-medium">{studentAnswers[index] ?? '-'}</span>
                <span className="text-green-700">Correct: {correct}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (activity.slideType === 'word_cloud') {
      const words = answer.words || [];
      return (
        <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">Your Words</h3>
          {words.length === 0 ? (
            <p className="text-sm text-muted-foreground">No words submitted.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {words.map((word: string, index: number) => (
                <Badge key={`${word}-${index}`} variant="outline" className="text-xs">
                  {word}
                </Badge>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (activity.slideType === 'poll') {
      const selectedIndex = answer.selectedOption ?? answer.answer;
      const optionLabel =
        typeof selectedIndex === 'number' && Array.isArray(content?.options)
          ? content.options[selectedIndex]
          : null;

      return (
        <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">Your Selection</h3>
          <p className="text-sm sm:text-base text-foreground">
            {optionLabel || 'No selection recorded.'}
          </p>
        </div>
      );
    }

    if (
      activity.slideType === 'matching_pairs' ||
      activity.slideType === 'drag_drop' ||
      activity.slideType === 'sequencing' ||
      activity.slideType === 'label_diagram' ||
      activity.slideType === 'collaborate_board'
    ) {
      return (
        <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">Your Response</h3>
          <pre className="text-xs sm:text-sm bg-muted/40 rounded-lg p-3 overflow-x-auto">
            {JSON.stringify(answer, null, 2)}
          </pre>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">{error}</h2>
          <Button onClick={() => navigate('/student/dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  if (!activity) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/student/dashboard')}
              className="rounded-xl text-xs sm:text-sm p-2"
              size="sm"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-lg sm:text-2xl font-semibold text-foreground">Activity Feedback</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">Review your submission and feedback</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Activity Header */}
        <div className="bg-card border border-border rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-1 sm:mb-2">
                {activity.title}
              </h1>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {activity.lessonTitle}
              </p>
            </div>
            <Badge
              className={`rounded-lg text-xs sm:text-sm px-2 py-1 ${
                autoGradedTypes.has(activity.slideType) && activity.graded
                  ? 'bg-blue-100 text-blue-700 border-blue-200'
                  : activity.graded
                  ? 'bg-purple-100 text-purple-700 border-purple-200'
                  : 'bg-orange-100 text-orange-700 border-orange-200'
              }`}
            >
              {autoGradedTypes.has(activity.slideType) && activity.graded
                ? 'Auto-graded'
                : activity.graded
                ? 'Teacher-graded'
                : 'Pending'}
            </Badge>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-muted-foreground">Score</p>
              <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />
            </div>
            {activity.score !== null && activity.maxScore !== null ? (
              <>
                <p className="text-2xl sm:text-3xl font-bold text-foreground">
                  {activity.score}/{activity.maxScore}
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {Math.round((activity.score / Math.max(activity.maxScore, 1)) * 100)}%
                </p>
              </>
            ) : (
              <p className="text-2xl sm:text-3xl font-bold text-muted-foreground">Pending</p>
            )}
          </div>

          <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-muted-foreground">Submitted</p>
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            </div>
            <p className="text-base sm:text-lg font-semibold text-foreground">
              {formatTimestamp(activity.createdAt)}
            </p>
          </div>

          <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-muted-foreground">Status</p>
              <Target className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
            </div>
            <p className="text-base sm:text-lg font-semibold text-foreground">
              {activity.graded ? 'Graded' : 'Awaiting review'}
            </p>
            <p className="text-xs sm:text-sm text-muted-foreground">Activity status</p>
          </div>
        </div>

        {/* Teacher Feedback */}
        {activity.answer?.feedback && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex items-start gap-2 sm:gap-3">
              <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-sm sm:text-base font-semibold text-blue-900 mb-1 sm:mb-2">Teacher Feedback</h3>
                <p className="text-xs sm:text-sm text-blue-800">{activity.answer.feedback}</p>
              </div>
            </div>
          </div>
        )}

        {/* Activity Specific Feedback */}
        {renderQuestionReview()}
        {renderAnswerDetails()}
      </div>
    </div>
  );
}
